import makeStyles from '@mui/styles/makeStyles';
import {Video} from "lucide-react";
import React from "react";
import {Link} from "react-router-dom";
import {color, font, radius, type} from "common/tokens";
import Card from "components/redesign/Card";
import CtaButton from "components/redesign/CtaButton";

const PANEL_WIDTH = 250;

const useStyles = makeStyles(theme => ({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: 26,
  },
  title: {
    ...type.pageTitle,
    color: color.TEXT,
    margin: 0,
  },
  card: {
    padding: 25,
    display: 'flex',
    alignItems: 'flex-start',
    gap: 32,
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
      gap: 20,
    },
  },
  // The ask itself, held apart from the terms it is asking about.
  prompt: {
    width: PANEL_WIDTH,
    flexShrink: 0,
    boxSizing: 'border-box',
    background: color.ACCENT_TINT,
    borderRadius: radius.CTA,
    padding: '62px 24px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 28,
    [theme.breakpoints.down('md')]: {
      width: '100%',
      padding: '32px 24px',
    },
  },
  promptIcon: {
    width: 70,
    height: 53,
    flexShrink: 0,
    borderRadius: radius.CTA,
    background: color.SURFACE,
    color: color.PURPLE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  promptText: {
    ...type.body,
    fontSize: 16,
    lineHeight: '20px',
    color: color.CARD_BODY,
    textAlign: 'center',
    margin: 0,
  },
  terms: {
    listStyle: 'none',
    margin: 0,
    padding: '0 16px',
    flex: '1 1 auto',
    maxWidth: 574,
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  term: {
    ...type.body,
    fontSize: 16,
    lineHeight: '20px',
    color: color.CARD_BODY,
    paddingLeft: 15,
    position: 'relative',
    '& strong': {
      fontWeight: font.SEMIBOLD,
      color: color.TEXT,
    },
    '&::before': {
      content: '"•"',
      position: 'absolute',
      left: 0,
      top: 0,
    },
  },
  privacy: {
    fontWeight: font.SEMIBOLD,
    color: color.PURPLE,
    textDecoration: 'none',
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: 24,
    flexWrap: 'wrap',
  },
  action: {
    minWidth: 240,
    padding: '20px 24px',
  },
}));

const Consent = ({onConsentSubmit}) => {
  const classes = useStyles();

  const choose = agreed => onConsentSubmit({key: agreed, label: agreed ? 'Agree' : 'Disagree'});

  const terms = [
    <>Recording is <strong>optional</strong> — you can continue the call even if you select “Disagree”.</>,
    <>The call is recorded only if <strong>all attendees select “Agree”</strong>, and you can <strong>manually start or stop recording</strong> anytime during the call.</>,
    <>Recordings are <strong>securely</strong> stored and are only accessible to <strong>authorized personnel under strict access controls</strong>. They are not exposed to other users.</>,
    <>The <strong>transcript and summary</strong> will be shared in your <strong>mentorship chat</strong> after the call.</>,
  ];

  return (
    <div className={classes.page}>
      <h1 className={classes.title}>Record this mentoring call?</h1>

      <Card variant={'panel'} bordered className={classes.card}>
        <div className={classes.prompt}>
          <span className={classes.promptIcon}>
            <Video size={24} strokeWidth={1.8}/>
          </span>
          <p className={classes.promptText}>
            We’d like to record this call to capture insights from your mentoring call.
            Recording does not affect call quality.
          </p>
        </div>

        <ul className={classes.terms}>
          {terms.map((term, index) => <li key={index} className={classes.term}>{term}</li>)}
          <li className={classes.term}>
            <Link to={'/privacypolicy'} target={'_blank'} className={classes.privacy}>
              View our Privacy Policy
            </Link>
          </li>
        </ul>
      </Card>

      <div className={classes.actions}>
        <CtaButton
          className={classes.action}
          size={'large'}
          variant={'primary'}
          label={'Agree'}
          onClick={() => choose(true)}
        />
        <CtaButton
          className={classes.action}
          size={'large'}
          variant={'secondary'}
          label={'Disagree'}
          onClick={() => choose(false)}
        />
      </div>
    </div>
  );
};

export default Consent;
